// src/components/PerformanceDashboard.tsx

import React, { useState, useEffect, useCallback } from "react";
import { getPerformanceStats, getStrategyPerformance, getModelPerformance, getRecommendations, deleteRecommendations, resetStats } from "../api/etfApi";
import type { PerformanceStats, StrategyPerformance, ModelPerformance, RecommendationTrack } from "../api/myTypes";

const PerformanceDashboard: React.FC = () => {
  const [overallStats, setOverallStats] = useState<PerformanceStats | null>(null);
  const [strategyStats, setStrategyStats] = useState<StrategyPerformance | null>(null);
  const [modelStats, setModelStats] = useState<ModelPerformance | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationTrack[]>([]);
  const [showOpenOnly, setShowOpenOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRec, setSelectedRec] = useState<RecommendationTrack | null>(null);
  const [selectedForDeletion, setSelectedForDeletion] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overall, strategies, models, recs] = await Promise.all([
        getPerformanceStats().catch(err => {
          console.error("Error loading performance stats:", err);
          return null;
        }),
        getStrategyPerformance().catch(err => {
          console.error("Error loading strategy performance:", err);
          return {};
        }),
        getModelPerformance().catch(err => {
          console.error("Error loading model performance:", err);
          return {};
        }),
        getRecommendations(!showOpenOnly).catch(err => {
          console.error("Error loading recommendations:", err);
          return { recommendations: [] };
        }),
      ]);
      
      if (overall) setOverallStats(overall);
      if (strategies) setStrategyStats(strategies);
      if (models) setModelStats(models);
      if (recs) {
        console.log("Loaded recommendations:", recs);
        const recsList = recs.recommendations || [];
        setRecommendations(recsList);
        // Dispatch event with recommendations data for the details panel
        window.dispatchEvent(new CustomEvent('recommendations-data', { detail: recsList }));
      }
    } catch (err: any) {
      console.error("Error loading stats:", err);
      setError(err.message || "Failed to load performance stats");
      // Still try to load recommendations even if stats fail
      try {
        const recs = await getRecommendations(!showOpenOnly);
        console.log("Loaded recommendations (fallback):", recs);
        setRecommendations(recs.recommendations || []);
      } catch (recErr: any) {
        console.error("Error loading recommendations:", recErr);
        setRecommendations([]);
      }
    } finally {
      setLoading(false);
    }
  }, [showOpenOnly]);

  useEffect(() => {
    loadStats();
    // Listen for refresh events (e.g. Track Trade button, Reset/Refresh stats)
    const handleRefresh = () => {
      loadStats();
    };
    window.addEventListener('refresh-performance', handleRefresh);
    return () => {
      window.removeEventListener('refresh-performance', handleRefresh);
    };
  }, [loadStats]);

  const handleToggleSelection = (recId: string) => {
    setSelectedForDeletion(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recId)) {
        newSet.delete(recId);
      } else {
        newSet.add(recId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedForDeletion.size === recommendations.length) {
      setSelectedForDeletion(new Set());
    } else {
      setSelectedForDeletion(new Set(recommendations.map(r => r.recommendation_id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedForDeletion.size === 0) return;
    
    if (!confirm(`Are you sure you want to delete ${selectedForDeletion.size} recommendation(s)? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const idsToDelete = Array.from(selectedForDeletion);
      await deleteRecommendations(idsToDelete);
      
      // Remove deleted recommendations from the list
      setRecommendations(prev => prev.filter(r => !selectedForDeletion.has(r.recommendation_id)));
      
      // Clear selection
      setSelectedForDeletion(new Set());
      
      // Clear selected rec if it was deleted
      if (selectedRec && selectedForDeletion.has(selectedRec.recommendation_id)) {
        setSelectedRec(null);
        window.dispatchEvent(new CustomEvent('recommendation-selected', { detail: null }));
      }
      
      // Reload stats to update counts
      await loadStats();
    } catch (err: any) {
      console.error("Error deleting recommendations:", err);
      alert(`Error deleting recommendations: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleResetStats = async () => {
    if (!showResetConfirm) {
      setShowResetConfirm(true);
      return;
    }

    setResetting(true);
    try {
      await resetStats();
      // Clear all local state
      setOverallStats(null);
      setStrategyStats(null);
      setModelStats(null);
      setRecommendations([]);
      setSelectedRec(null);
      setSelectedForDeletion(new Set());
      window.dispatchEvent(new CustomEvent('recommendation-selected', { detail: null }));
      
      // Reload stats (will show empty state)
      await loadStats();
      
      setShowResetConfirm(false);
      alert("All performance statistics have been reset successfully.");
    } catch (err: any) {
      console.error("Error resetting stats:", err);
      alert(`Error resetting statistics: ${err.message}`);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ padding: "2rem", textAlign: "center" }}>
        <p>Loading performance statistics...</p>
      </div>
    );
  }

  // Don't return early on error - still show recommendations section

  // Don't return early - always show the recommendations section even if stats are insufficient

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {/* Header with Refresh and Reset Buttons */}
      <div className="card" style={{ padding: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ color: "#ffffff" }}>Performance Dashboard</h3>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button
              className="primary-button"
              onClick={loadStats}
              disabled={loading}
              style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
            >
              {loading ? "Refreshing..." : "Refresh Stats"}
            </button>
            {showResetConfirm ? (
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <span style={{ color: "#ef4444", fontSize: "0.875rem", marginRight: "0.5rem" }}>
                  Reset all stats?
                </span>
                <button
                  className="primary-button"
                  onClick={handleResetStats}
                  disabled={resetting}
                  style={{ 
                    fontSize: "0.875rem", 
                    padding: "0.5rem 1rem",
                    backgroundColor: "#ef4444",
                    borderColor: "#ef4444"
                  }}
                >
                  {resetting ? "Resetting..." : "Confirm Reset"}
                </button>
                <button
                  className="primary-button"
                  onClick={() => setShowResetConfirm(false)}
                  disabled={resetting}
                  style={{ 
                    fontSize: "0.875rem", 
                    padding: "0.5rem 1rem",
                    backgroundColor: "#6b7280",
                    borderColor: "#6b7280"
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className="primary-button"
                onClick={handleResetStats}
                disabled={resetting || loading}
                style={{ 
                  fontSize: "0.875rem", 
                  padding: "0.5rem 1rem",
                  backgroundColor: "#ef4444",
                  borderColor: "#ef4444"
                }}
              >
                Reset Stats
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card" style={{ padding: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <div style={{ color: "#ef4444", marginBottom: "0.5rem" }}>Error: {error}</div>
          <button className="primary-button" onClick={loadStats} style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}>
            Retry
          </button>
        </div>
      )}

      {/* Insufficient Data Message */}
      {overallStats?.insufficient_data && (
        <div className="card" style={{ padding: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <p style={{ color: "#ffffff" }}>{overallStats.message || "Insufficient data to display statistics"}</p>
          <p style={{ color: "#9ca3af", fontSize: "0.875rem", marginTop: "0.5rem" }}>
            Track more trades to see performance statistics.
          </p>
        </div>
      )}

      {/* Overall Statistics */}
      {overallStats && !overallStats.insufficient_data && (
        <div className="card" style={{ padding: "1.5rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Overall Performance</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem" }}>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Win Rate</div>
              <div style={{ color: "#ffffff", fontSize: "1.5rem", fontWeight: "bold" }}>
                {overallStats.win_rate}%
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Total Trades</div>
              <div style={{ color: "#ffffff", fontSize: "1.5rem", fontWeight: "bold" }}>
                {overallStats.closed_positions}
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Wins</div>
              <div style={{ color: "#10b981", fontSize: "1.5rem", fontWeight: "bold" }}>
                {overallStats.wins}
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Losses</div>
              <div style={{ color: "#ef4444", fontSize: "1.5rem", fontWeight: "bold" }}>
                {overallStats.losses}
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Avg Return</div>
              <div style={{ color: overallStats.avg_return >= 0 ? "#10b981" : "#ef4444", fontSize: "1.5rem", fontWeight: "bold" }}>
                {overallStats.avg_return >= 0 ? "+" : ""}{Number(overallStats.avg_return).toFixed(2)}%
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Total Return</div>
              <div style={{ color: overallStats.total_return >= 0 ? "#10b981" : "#ef4444", fontSize: "1.5rem", fontWeight: "bold" }}>
                {overallStats.total_return >= 0 ? "+" : ""}{Number(overallStats.total_return).toFixed(2)}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Performance */}
      {strategyStats && Object.keys(strategyStats).length > 0 && (
        <div className="card" style={{ padding: "1.5rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Strategy Performance</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {Object.entries(strategyStats).map(([strategy, stats]) => (
              <div key={strategy} style={{ padding: "0.75rem", backgroundColor: "#0f1117", borderRadius: "4px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                  <span style={{ color: "#ffffff", fontWeight: "bold" }}>{strategy}</span>
                  <span style={{ color: "#10b981", fontWeight: "bold" }}>{stats.win_rate}% Win Rate</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem", fontSize: "0.875rem" }}>
                  <div>
                    <span style={{ color: "#9ca3af" }}>Trades: </span>
                    <span style={{ color: "#ffffff" }}>{stats.closed_positions}</span>
                  </div>
                  <div>
                    <span style={{ color: "#9ca3af" }}>Wins: </span>
                    <span style={{ color: "#10b981" }}>{stats.wins}</span>
                  </div>
                  <div>
                    <span style={{ color: "#9ca3af" }}>Losses: </span>
                    <span style={{ color: "#ef4444" }}>{stats.losses}</span>
                  </div>
                  <div>
                    <span style={{ color: "#9ca3af" }}>Avg: </span>
                    <span style={{ color: stats.avg_return >= 0 ? "#10b981" : "#ef4444" }}>
                      {stats.avg_return >= 0 ? "+" : ""}{Number(stats.avg_return).toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Model Performance */}
      {modelStats && Object.keys(modelStats).length > 0 && (
        <div className="card" style={{ padding: "1.5rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Model Performance</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {Object.entries(modelStats).map(([model, stats]) => (
              <div key={model} style={{ padding: "0.75rem", backgroundColor: "#0f1117", borderRadius: "4px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                  <span style={{ color: "#ffffff", fontWeight: "bold" }}>{model}</span>
                  <span style={{ color: "#10b981", fontWeight: "bold" }}>{stats.win_rate}% Win Rate</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem", fontSize: "0.875rem" }}>
                  <div>
                    <span style={{ color: "#9ca3af" }}>Trades: </span>
                    <span style={{ color: "#ffffff" }}>{stats.closed_positions}</span>
                  </div>
                  <div>
                    <span style={{ color: "#9ca3af" }}>Wins: </span>
                    <span style={{ color: "#10b981" }}>{stats.wins}</span>
                  </div>
                  <div>
                    <span style={{ color: "#9ca3af" }}>Losses: </span>
                    <span style={{ color: "#ef4444" }}>{stats.losses}</span>
                  </div>
                  <div>
                    <span style={{ color: "#9ca3af" }}>Avg: </span>
                    <span style={{ color: stats.avg_return >= 0 ? "#10b981" : "#ef4444" }}>
                      {stats.avg_return >= 0 ? "+" : ""}{stats.avg_return}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tracked Recommendations List - Always show this section */}
      <div className="card" style={{ padding: "1.5rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ color: "#ffffff" }}>
            Tracked Recommendations ({recommendations.length})
            {loading && <span style={{ color: "#9ca3af", fontSize: "0.875rem", marginLeft: "0.5rem" }}>(Loading...)</span>}
            {selectedForDeletion.size > 0 && (
              <span style={{ color: "#ef4444", fontSize: "0.875rem", marginLeft: "0.5rem", fontWeight: "normal" }}>
                ({selectedForDeletion.size} selected)
              </span>
            )}
          </h3>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            {selectedForDeletion.size > 0 && (
              <button
                className="primary-button"
                onClick={handleDeleteSelected}
                disabled={deleting}
                style={{ 
                  fontSize: "0.875rem", 
                  padding: "0.5rem 1rem",
                  backgroundColor: "#ef4444",
                  borderColor: "#ef4444"
                }}
              >
                {deleting ? "Deleting..." : `Delete Selected (${selectedForDeletion.size})`}
              </button>
            )}
            <label style={{ color: "#9ca3af", fontSize: "0.875rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={showOpenOnly}
                onChange={async (e) => {
                  const newValue = e.target.checked;
                  setShowOpenOnly(newValue);
                  setSelectedForDeletion(new Set()); // Clear selection when filtering
                  try {
                    const recs = await getRecommendations(!newValue);
                    setRecommendations(recs.recommendations);
                  } catch (err: any) {
                    console.error("Failed to load recommendations:", err);
                  }
                }}
                style={{ cursor: "pointer" }}
              />
              Show open only
            </label>
          </div>
        </div>
        {recommendations.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "#9ca3af" }}>
            <p>No tracked recommendations found.</p>
            <p style={{ fontSize: "0.875rem", marginTop: "0.5rem" }}>
              Use "Track Trade" button on recommendations to start tracking.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", maxHeight: "600px", overflowY: "auto" }}>
            {/* Select All Checkbox */}
            {recommendations.length > 0 && (
              <div style={{ padding: "0.5rem", backgroundColor: "#0f1117", borderRadius: "4px", border: "1px solid #2c3142" }}>
                <label style={{ color: "#9ca3af", fontSize: "0.875rem", display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={selectedForDeletion.size === recommendations.length && recommendations.length > 0}
                    onChange={handleSelectAll}
                    style={{ cursor: "pointer" }}
                  />
                  Select All ({recommendations.length})
                </label>
              </div>
            )}
            {recommendations.map((rec) => {
              const outcomeColor = 
                rec.outcome === "win" ? "#10b981" :
                rec.outcome === "loss" ? "#ef4444" :
                rec.outcome === "breakeven" ? "#f59e0b" :
                "#6b7280";
              
              const returnColor = rec.return_pct !== undefined 
                ? (rec.return_pct >= 0 ? "#10b981" : "#ef4444")
                : "#9ca3af";

              const isSelected = selectedRec?.recommendation_id === rec.recommendation_id;

              const isSelectedForDeletion = selectedForDeletion.has(rec.recommendation_id);

              return (
                <div
                  key={rec.recommendation_id}
                  onClick={(e) => {
                    // Don't trigger selection if clicking on checkbox
                    if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).closest('label')) {
                      return;
                    }
                    const newSelection = isSelected ? null : rec;
                    setSelectedRec(newSelection);
                    // Dispatch custom event for details panel
                    window.dispatchEvent(new CustomEvent('recommendation-selected', { detail: newSelection }));
                  }}
                  style={{
                    padding: "1rem",
                    backgroundColor: isSelected ? "#1a3a5c" : isSelectedForDeletion ? "#2a1a1a" : "#0f1117",
                    borderRadius: "4px",
                    border: isSelected ? "2px solid #3b82f6" : isSelectedForDeletion ? "2px solid #ef4444" : "1px solid #2c3142",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected && !isSelectedForDeletion) {
                      e.currentTarget.style.backgroundColor = "#1a1d29";
                      e.currentTarget.style.borderColor = "#3b82f6";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected && !isSelectedForDeletion) {
                      e.currentTarget.style.backgroundColor = "#0f1117";
                      e.currentTarget.style.borderColor = "#2c3142";
                    }
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <label 
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelectedForDeletion}
                          onChange={() => handleToggleSelection(rec.recommendation_id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: "pointer", marginRight: "0.5rem" }}
                        />
                      </label>
                      <span style={{ color: "#ffffff", fontWeight: "bold", fontSize: "1.1rem" }}>{rec.ticker}</span>
                      <span
                        style={{
                          padding: "0.25rem 0.5rem",
                          borderRadius: "4px",
                          backgroundColor: outcomeColor,
                          color: "white",
                          fontSize: "0.75rem",
                          fontWeight: "bold"
                        }}
                      >
                        {rec.outcome?.toUpperCase() || "OPEN"}
                      </span>
                      <span style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                        {rec.strategy || 'N/A'} / {rec.model || 'N/A'}
                      </span>
                    </div>
                    <span style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                      {rec.created_at ? new Date(rec.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "0.75rem", fontSize: "0.875rem" }}>
                    {rec.entry_price != null && typeof rec.entry_price === 'number' && (
                      <div>
                        <span style={{ color: "#9ca3af" }}>Entry: </span>
                        <span style={{ color: "#ffffff" }}>${rec.entry_price.toFixed(2)}</span>
                      </div>
                    )}
                    {rec.exit_price != null && typeof rec.exit_price === 'number' && (
                      <div>
                        <span style={{ color: "#9ca3af" }}>Exit: </span>
                        <span style={{ color: "#ffffff" }}>${rec.exit_price.toFixed(2)}</span>
                      </div>
                    )}
                    {rec.return_pct != null && typeof rec.return_pct === 'number' && (
                      <div>
                        <span style={{ color: "#9ca3af" }}>Return: </span>
                        <span style={{ color: returnColor, fontWeight: "bold" }}>
                          {rec.return_pct >= 0 ? "+" : ""}{rec.return_pct.toFixed(2)}%
                        </span>
                      </div>
                    )}
                    {rec.entry_timestamp && (
                      <div>
                        <span style={{ color: "#9ca3af" }}>Opened: </span>
                        <span style={{ color: "#ffffff" }}>
                          {new Date(rec.entry_timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {rec.exit_timestamp && (
                      <div>
                        <span style={{ color: "#9ca3af" }}>Closed: </span>
                        <span style={{ color: "#ffffff" }}>
                          {new Date(rec.exit_timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                  {rec.recommendation_data?.text_reasoning && typeof rec.recommendation_data.text_reasoning === 'string' && (
                    <div style={{ marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "1px solid #2c3142" }}>
                      <div style={{ color: "#9ca3af", fontSize: "0.8rem", fontStyle: "italic" }}>
                        {rec.recommendation_data.text_reasoning.substring(0, 150)}
                        {rec.recommendation_data.text_reasoning.length > 150 ? "..." : ""}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Details panel component
const PerformanceDashboardDetails: React.FC = () => {
  const [selectedRec, setSelectedRec] = React.useState<RecommendationTrack | null>(null);
  const [closedPositions, setClosedPositions] = React.useState<RecommendationTrack[]>([]);

  // Listen for selection changes via custom event
  React.useEffect(() => {
    const handleSelection = (event: CustomEvent<RecommendationTrack | null>) => {
      setSelectedRec(event.detail);
    };
    
    window.addEventListener('recommendation-selected' as any, handleSelection as EventListener);
    return () => {
      window.removeEventListener('recommendation-selected' as any, handleSelection as EventListener);
    };
  }, []);

  // Listen for recommendations updates to get closed positions
  React.useEffect(() => {
    const handleRecommendationsData = (event: CustomEvent<RecommendationTrack[]>) => {
      const closed = event.detail
        .filter(rec => rec.exit_timestamp && rec.outcome && rec.outcome !== "open")
        .sort((a, b) => {
          const dateA = new Date(a.exit_timestamp || 0).getTime();
          const dateB = new Date(b.exit_timestamp || 0).getTime();
          return dateB - dateA; // Most recent first
        });
      setClosedPositions(closed);
    };

    window.addEventListener('recommendations-data' as any, handleRecommendationsData as EventListener);
    
    return () => {
      window.removeEventListener('recommendations-data' as any, handleRecommendationsData as EventListener);
    };
  }, []);

  const handlePrintTable = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const tableRows = closedPositions.map((pos, idx) => {
      const entryDate = pos.entry_timestamp ? new Date(pos.entry_timestamp).toLocaleDateString() : 'N/A';
      const exitDate = pos.exit_timestamp ? new Date(pos.exit_timestamp).toLocaleDateString() : 'N/A';
      const entryPrice = pos.entry_price?.toFixed(2) || 'N/A';
      const exitPrice = pos.exit_price?.toFixed(2) || 'N/A';
      const returnPct = pos.return_pct !== undefined ? `${pos.return_pct >= 0 ? '+' : ''}${pos.return_pct.toFixed(2)}%` : 'N/A';
      const outcome = pos.outcome?.toUpperCase() || 'N/A';
      
      return `
        <tr>
          <td>${idx + 1}</td>
          <td>${pos.ticker}</td>
          <td>${entryDate}</td>
          <td>$${entryPrice}</td>
          <td>${exitDate}</td>
          <td>$${exitPrice}</td>
          <td>${returnPct}</td>
          <td>${outcome}</td>
          <td>${pos.strategy || 'N/A'}</td>
          <td>${pos.model || 'N/A'}</td>
        </tr>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Closed Positions History</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
            }
            h1 {
              color: #333;
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .win { color: #10b981; font-weight: bold; }
            .loss { color: #ef4444; font-weight: bold; }
            .breakeven { color: #f59e0b; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Closed Positions History</h1>
          <p>Generated on: ${new Date().toLocaleString()}</p>
          <p>Total Closed Positions: ${closedPositions.length}</p>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Ticker</th>
                <th>Entry Date</th>
                <th>Entry Price</th>
                <th>Exit Date</th>
                <th>Exit Price</th>
                <th>Return %</th>
                <th>Outcome</th>
                <th>Strategy</th>
                <th>Model</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const outcomeColor = selectedRec
    ? (selectedRec.outcome === "win" ? "#10b981" :
       selectedRec.outcome === "loss" ? "#ef4444" :
       selectedRec.outcome === "breakeven" ? "#f59e0b" :
       "#6b7280")
    : "#6b7280";

  const returnColor = selectedRec && selectedRec.return_pct !== undefined 
    ? (selectedRec.return_pct >= 0 ? "#10b981" : "#ef4444")
    : "#9ca3af";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {/* Closed Positions Table */}
      <div className="card" style={{ padding: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
          <h3 style={{ color: "#ffffff", fontSize: "1rem" }}>Closed Positions History</h3>
          {closedPositions.length > 0 && (
            <button
              className="primary-button"
              onClick={handlePrintTable}
              style={{ 
                fontSize: "0.75rem", 
                padding: "0.4rem 0.8rem",
                backgroundColor: "#3b82f6",
                borderColor: "#3b82f6"
              }}
            >
              Print
            </button>
          )}
        </div>
        {closedPositions.length === 0 ? (
          <div style={{ padding: "1rem", textAlign: "center", color: "#9ca3af", fontSize: "0.875rem" }}>
            No closed positions yet
          </div>
        ) : (
          <div style={{ 
            maxHeight: "350px", 
            overflowY: "auto",
            overflowX: "auto",
            border: "1px solid #2c3142",
            borderRadius: "4px",
            backgroundColor: "#0f1117"
          }}>
            <table style={{ 
              width: "100%", 
              borderCollapse: "collapse",
              fontSize: "0.75rem",
              minWidth: "800px"
            }}>
              <thead style={{ position: "sticky", top: 0, backgroundColor: "#0f1117", zIndex: 10 }}>
                <tr>
                  <th style={{ 
                    padding: "0.5rem", 
                    textAlign: "left", 
                    borderBottom: "2px solid #2c3142",
                    color: "#ffffff",
                    fontWeight: "bold",
                    backgroundColor: "#0f1117"
                  }}>#</th>
                  <th style={{ 
                    padding: "0.5rem", 
                    textAlign: "left", 
                    borderBottom: "2px solid #2c3142",
                    color: "#ffffff",
                    fontWeight: "bold",
                    backgroundColor: "#0f1117"
                  }}>Ticker</th>
                  <th style={{ 
                    padding: "0.5rem", 
                    textAlign: "left", 
                    borderBottom: "2px solid #2c3142",
                    color: "#ffffff",
                    fontWeight: "bold",
                    backgroundColor: "#0f1117"
                  }}>Entry Date</th>
                  <th style={{ 
                    padding: "0.5rem", 
                    textAlign: "right", 
                    borderBottom: "2px solid #2c3142",
                    color: "#ffffff",
                    fontWeight: "bold",
                    backgroundColor: "#0f1117"
                  }}>Entry $</th>
                  <th style={{ 
                    padding: "0.5rem", 
                    textAlign: "left", 
                    borderBottom: "2px solid #2c3142",
                    color: "#ffffff",
                    fontWeight: "bold",
                    backgroundColor: "#0f1117"
                  }}>Exit Date</th>
                  <th style={{ 
                    padding: "0.5rem", 
                    textAlign: "right", 
                    borderBottom: "2px solid #2c3142",
                    color: "#ffffff",
                    fontWeight: "bold",
                    backgroundColor: "#0f1117"
                  }}>Exit $</th>
                  <th style={{ 
                    padding: "0.5rem", 
                    textAlign: "right", 
                    borderBottom: "2px solid #2c3142",
                    color: "#ffffff",
                    fontWeight: "bold",
                    backgroundColor: "#0f1117"
                  }}>Return %</th>
                  <th style={{ 
                    padding: "0.5rem", 
                    textAlign: "center", 
                    borderBottom: "2px solid #2c3142",
                    color: "#ffffff",
                    fontWeight: "bold",
                    backgroundColor: "#0f1117"
                  }}>Outcome</th>
                  <th style={{ 
                    padding: "0.5rem", 
                    textAlign: "left", 
                    borderBottom: "2px solid #2c3142",
                    color: "#ffffff",
                    fontWeight: "bold",
                    backgroundColor: "#0f1117"
                  }}>Strategy</th>
                </tr>
              </thead>
              <tbody>
                {closedPositions.map((pos, idx) => {
                  const entryDate = pos.entry_timestamp ? new Date(pos.entry_timestamp).toLocaleDateString() : 'N/A';
                  const exitDate = pos.exit_timestamp ? new Date(pos.exit_timestamp).toLocaleDateString() : 'N/A';
                  const returnColor = pos.return_pct !== undefined 
                    ? (pos.return_pct >= 0 ? "#10b981" : "#ef4444")
                    : "#9ca3af";
                  const outcomeColor = 
                    pos.outcome === "win" ? "#10b981" :
                    pos.outcome === "loss" ? "#ef4444" :
                    pos.outcome === "breakeven" ? "#f59e0b" :
                    "#6b7280";
                  
                  return (
                    <tr 
                      key={pos.recommendation_id}
                      style={{
                        borderBottom: "1px solid #2c3142",
                        backgroundColor: idx % 2 === 0 ? "#0f1117" : "#1a1d29"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#1a3a5c";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#0f1117" : "#1a1d29";
                      }}
                    >
                      <td style={{ padding: "0.5rem", color: "#9ca3af" }}>{idx + 1}</td>
                      <td style={{ padding: "0.5rem", color: "#ffffff", fontWeight: "bold" }}>{pos.ticker}</td>
                      <td style={{ padding: "0.5rem", color: "#9ca3af" }}>{entryDate}</td>
                      <td style={{ padding: "0.5rem", color: "#ffffff", textAlign: "right" }}>
                        ${pos.entry_price?.toFixed(2) || 'N/A'}
                      </td>
                      <td style={{ padding: "0.5rem", color: "#9ca3af" }}>{exitDate}</td>
                      <td style={{ padding: "0.5rem", color: "#ffffff", textAlign: "right" }}>
                        ${pos.exit_price?.toFixed(2) || 'N/A'}
                      </td>
                      <td style={{ 
                        padding: "0.5rem", 
                        color: returnColor, 
                        textAlign: "right",
                        fontWeight: "bold"
                      }}>
                        {pos.return_pct !== undefined 
                          ? `${pos.return_pct >= 0 ? '+' : ''}${pos.return_pct.toFixed(2)}%`
                          : 'N/A'}
                      </td>
                      <td style={{ 
                        padding: "0.5rem", 
                        textAlign: "center"
                      }}>
                        <span style={{
                          padding: "0.2rem 0.5rem",
                          borderRadius: "4px",
                          backgroundColor: outcomeColor,
                          color: "white",
                          fontSize: "0.7rem",
                          fontWeight: "bold"
                        }}>
                          {pos.outcome?.toUpperCase() || 'N/A'}
                        </span>
                      </td>
                      <td style={{ padding: "0.5rem", color: "#9ca3af" }}>{pos.strategy || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {closedPositions.length > 10 && (
              <div style={{ 
                padding: "0.5rem", 
                textAlign: "center", 
                color: "#9ca3af", 
                fontSize: "0.75rem",
                borderTop: "1px solid #2c3142",
                backgroundColor: "#0f1117"
              }}>
                Showing all {closedPositions.length} closed positions. Scroll to see more.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recommendation Details Section */}
      {selectedRec && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3 style={{ color: "#ffffff" }}>Recommendation Details</h3>
            <button
              className="primary-button"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('recommendation-selected', { detail: null }));
                setSelectedRec(null);
              }}
              style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
            >
              Close
            </button>
          </div>

      <div className="card" style={{ padding: "1.5rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem", paddingBottom: "1rem", borderBottom: "1px solid #2c3142" }}>
          <span style={{ color: "#ffffff", fontWeight: "bold", fontSize: "1.5rem" }}>{selectedRec.ticker}</span>
          <span
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "6px",
              backgroundColor: outcomeColor,
              color: "white",
              fontSize: "0.875rem",
              fontWeight: "bold"
            }}
          >
            {selectedRec.outcome?.toUpperCase() || "OPEN"}
          </span>
          <span style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
            {selectedRec.strategy || 'N/A'} / {selectedRec.model || 'N/A'}
          </span>
        </div>

        {/* Trade Details */}
        <div style={{ marginBottom: "1.5rem" }}>
          <h4 style={{ color: "#ffffff", marginBottom: "0.75rem" }}>Trade Details</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem" }}>
            {selectedRec.entry_price != null && typeof selectedRec.entry_price === 'number' && (
              <div>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Entry Price</div>
                <div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "bold" }}>
                  ${selectedRec.entry_price.toFixed(2)}
                </div>
              </div>
            )}
            {selectedRec.exit_price != null && typeof selectedRec.exit_price === 'number' && (
              <div>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Exit Price</div>
                <div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "bold" }}>
                  ${selectedRec.exit_price.toFixed(2)}
                </div>
              </div>
            )}
            {selectedRec.return_pct != null && typeof selectedRec.return_pct === 'number' && (
              <div>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Return</div>
                <div style={{ color: returnColor, fontSize: "1.25rem", fontWeight: "bold" }}>
                  {selectedRec.return_pct >= 0 ? "+" : ""}{selectedRec.return_pct.toFixed(2)}%
                </div>
              </div>
            )}
            {selectedRec.entry_timestamp && (
              <div>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Entry Date</div>
                <div style={{ color: "#ffffff", fontSize: "1rem" }}>
                  {new Date(selectedRec.entry_timestamp).toLocaleString()}
                </div>
              </div>
            )}
            {selectedRec.exit_timestamp && (
              <div>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Exit Date</div>
                <div style={{ color: "#ffffff", fontSize: "1rem" }}>
                  {new Date(selectedRec.exit_timestamp).toLocaleString()}
                </div>
              </div>
            )}
            {selectedRec.created_at && (
              <div>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Tracked On</div>
                <div style={{ color: "#ffffff", fontSize: "1rem" }}>
                  {new Date(selectedRec.created_at).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recommendation Data */}
        {selectedRec.recommendation_data && (
          <div style={{ marginBottom: "1.5rem" }}>
            <h4 style={{ color: "#ffffff", marginBottom: "0.75rem" }}>Recommendation Analysis</h4>
            <div style={{ padding: "1rem", backgroundColor: "#0f1117", borderRadius: "4px", border: "1px solid #2c3142" }}>
              {selectedRec.recommendation_data.text_reasoning && typeof selectedRec.recommendation_data.text_reasoning === 'string' && (
                <div style={{ marginBottom: "1rem" }}>
                  <div style={{ color: "#9ca3af", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Reasoning:</div>
                  <div style={{ color: "#ffffff", whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
                    {selectedRec.recommendation_data.text_reasoning}
                  </div>
                </div>
              )}
              {selectedRec.recommendation_data.entry_zone && (
                <div style={{ marginBottom: "0.5rem" }}>
                  <span style={{ color: "#9ca3af" }}>Entry Zone: </span>
                  <span style={{ color: "#ffffff" }}>{selectedRec.recommendation_data.entry_zone}</span>
                </div>
              )}
              {selectedRec.recommendation_data.exit_zone && (
                <div style={{ marginBottom: "0.5rem" }}>
                  <span style={{ color: "#9ca3af" }}>Exit Zone: </span>
                  <span style={{ color: "#ffffff" }}>{selectedRec.recommendation_data.exit_zone}</span>
                </div>
              )}
              {selectedRec.recommendation_data.stop_loss && (
                <div style={{ marginBottom: "0.5rem" }}>
                  <span style={{ color: "#9ca3af" }}>Stop Loss: </span>
                  <span style={{ color: "#ef4444" }}>{selectedRec.recommendation_data.stop_loss}</span>
                </div>
              )}
              {selectedRec.recommendation_data.take_profit && (
                <div style={{ marginBottom: "0.5rem" }}>
                  <span style={{ color: "#9ca3af" }}>Take Profit: </span>
                  <span style={{ color: "#10b981" }}>{selectedRec.recommendation_data.take_profit}</span>
                </div>
              )}
              {selectedRec.recommendation_data.position_size && (
                <div style={{ marginBottom: "0.5rem" }}>
                  <span style={{ color: "#9ca3af" }}>Position Size: </span>
                  <span style={{ color: "#ffffff" }}>{selectedRec.recommendation_data.position_size}</span>
                </div>
              )}
              {selectedRec.recommendation_data.rating && (
                <div style={{ marginBottom: "0.5rem" }}>
                  <span style={{ color: "#9ca3af" }}>Rating: </span>
                  <span style={{ color: "#ffffff", fontWeight: "bold" }}>{selectedRec.recommendation_data.rating}</span>
                </div>
              )}
              {selectedRec.recommendation_data.conviction && (
                <div style={{ marginBottom: "0.5rem" }}>
                  <span style={{ color: "#9ca3af" }}>Conviction: </span>
                  <span style={{ color: "#ffffff" }}>{selectedRec.recommendation_data.conviction}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Market Regime */}
        {selectedRec.market_regime && (
          <div style={{ marginBottom: "1.5rem" }}>
            <h4 style={{ color: "#ffffff", marginBottom: "0.75rem" }}>Market Regime</h4>
            <div style={{ padding: "1rem", backgroundColor: "#0f1117", borderRadius: "4px", border: "1px solid #2c3142" }}>
              {selectedRec.market_regime.regime_type && (
                <div style={{ marginBottom: "0.5rem" }}>
                  <span style={{ color: "#9ca3af" }}>Regime: </span>
                  <span style={{ color: "#ffffff", fontWeight: "bold" }}>{selectedRec.market_regime.regime_type}</span>
                </div>
              )}
              {selectedRec.market_regime.regime_description && (
                <div style={{ color: "#ffffff", fontSize: "0.875rem", marginTop: "0.5rem" }}>
                  {selectedRec.market_regime.regime_description}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Full Data (Collapsible) */}
        <div>
          <details style={{ color: "#ffffff" }}>
            <summary style={{ cursor: "pointer", color: "#9ca3af", marginBottom: "0.5rem" }}>
              View Full Recommendation Data
            </summary>
            <pre style={{ 
              padding: "1rem", 
              backgroundColor: "#0f1117", 
              borderRadius: "4px", 
              border: "1px solid #2c3142",
              overflow: "auto",
              fontSize: "0.8rem",
              color: "#ffffff",
              maxHeight: "400px"
            }}>
              {JSON.stringify(selectedRec, null, 2)}
            </pre>
          </details>
        </div>
      </div>
        </>
      )}
      
      {!selectedRec && (
        <div style={{ padding: "2rem", textAlign: "center", color: "#9ca3af" }}>
          <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Recommendation Details</h3>
          <p>Select a tracked recommendation from the list to view full details.</p>
        </div>
      )}
    </div>
  );
};

export default PerformanceDashboard;
export { PerformanceDashboardDetails };

